import api from '@/lib/api';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import type { ApiError } from '@/lib/api';
import type { PathwayOption } from '@/components/dashboard/PathwayModal';

export interface User {
  id: string;
  email: string;
  name: string;
  accessToken: string;
}

export interface UpdateUserDto {
  username?: string;
  email?: string;
  password?: string;
  profilePicture?: string | null;
  isTwoFactorEnabled?: boolean;
  referenceHistory?: boolean;
  referenceMemory?: boolean;
  isTwoFactorVerified?: boolean;
  userStatus?: boolean;
}

export interface StripeCustomerMetadata {
  name: string;
}

export interface StripeCustomerSnapshot {
  id: string;
  email: string;
  name: string;
}

export interface UserData {
  _id: string;
  username: string;
  displayName?: string;
  email: string;
  password?: string; // Password should be optional, as it's usually not returned in responses
  profilePicture?: string | null;

  // Flags & booleans
  isTwoFactorEnabled?: boolean;
  isTwoFactorVerified?: boolean;
  referenceHistory?: boolean;
  referenceMemory?: boolean;
  consent?: boolean;
  isSuperAdmin?: boolean;
  isTemporaryUser?: boolean;
  userStatus?: boolean;
  pathwayIds: PathwayOption[];
  isSubscribed: boolean;

  // Stripe integration
  stripeCustomerCreated?: string; // ISO date string
  stripeCustomerEmail?: string;
  stripeCustomerId?: string;
  stripeCustomerMetadata?: StripeCustomerMetadata;
  stripeCustomerName?: string;
  stripeCustomerSnapshot?: StripeCustomerSnapshot;

  // Additional metadata
  loginCount?: number;
  createdAt?: string; // ISO date string
  updatedAt?: string; // ISO date string
}

interface UpdateUserPayload {
  id: string;
  dto: UpdateUserDto;
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  userDetails: UserData | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  loading: false,
  error: null,
  userDetails: null,
};

export const register = createAsyncThunk(
  'auth/register',
  async (
    userData: {
      email: string;
      password: string;
      displayName: string;
      username: string;
      consent: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_API_URL}/auth/registration`,
        userData
      );
      return response.data;
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(
        err.response?.data?.message || 'Registration failed'
      );
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (
    credentials: { email: string; password: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_API_URL}/auth/login`,
        credentials
      );
      return response.data;
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(err.response?.data?.message || 'Login failed');
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgot-password',
  async (credentials: { email: string }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_API_URL}/auth/forget-password`,
        credentials
      );
      return response.data;
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(err.response?.data?.message || 'Login failed');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/reset-password',
  async (
    credentials: { token: string; newPassword: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_API_URL}/auth/reset-password`,
        credentials
      );
      return response.data;
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(
        err.response?.data?.message || 'Reset password failed'
      );
    }
  }
);

export const fetchUserProfile = createAsyncThunk(
  'auth/fetchUserProfile',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/users/list/${id}`);
      return data;
    } catch (err: unknown) {
      const error = err as ApiError;
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (
    { id, formData }: { id: string; formData: FormData },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await api.put(`/users/update/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data;
    } catch (err: unknown) {
      const error = err as ApiError;
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateUser = createAsyncThunk(
  'auth/updateUserProfile',
  async ({ id, dto }: UpdateUserPayload, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/users/${id}`, dto);
      return data;
    } catch (err: unknown) {
      const error = err as ApiError;
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
        // state.user = action.payload.user;
        // state.token = action.payload.token;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Profile Fetch
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.userDetails = action.payload;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Profile Update
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.userDetails = action.payload;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
